// src/features/monetize/components/OfferDrawer.tsx

import { useEffect } from 'react';
import { Button, Divider, Drawer, Group, NumberInput, SegmentedControl, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useController, useForm, useWatch, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTimeField } from '@/shared/components/DateTimeField';
import { MONETIZE_CONSTRAINTS, OFFER_DURATIONS, OFFER_DURATION_LABELS } from '@/lib/constants/monetize';
import type { MonetizeOffer, MonetizeProduct } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { offerFormSchema, type OfferFormValues } from '../schemas/monetizeSchemas';
import { useCreateOffer, useUpdateOffer } from '../useMonetizeMutations';

interface OfferDrawerProps {
  opened: boolean;
  offer: MonetizeOffer | null;
  products: MonetizeProduct[];
  onClose: () => void;
}

export function OfferDrawer({ opened, offer, products, onClose }: OfferDrawerProps) {
  const isCreate = offer === null;
  const createMutation = useCreateOffer();
  const updateMutation = useUpdateOffer();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<OfferFormValues>({ resolver: zodResolver(offerFormSchema), defaultValues: MonetizeUtils.defaultOfferForm() });

  useEffect(() => {
    if (opened) form.reset(offer ? MonetizeUtils.offerToForm(offer) : MonetizeUtils.defaultOfferForm());
  }, [opened, offer, form]);

  const discountType = useWatch({ control: form.control, name: 'discountType' });
  const duration = useWatch({ control: form.control, name: 'duration' });

  async function handleSubmit(values: OfferFormValues) {
    try {
      if (isCreate) {
        const res = await createMutation.mutateAsync(MonetizeUtils.formToOfferReq(values));
        notifications.show({ title: 'Created', message: `Code ${res.offer.code} is live.`, color: 'green' });
      } else {
        await updateMutation.mutateAsync({ offerId: offer.id, req: MonetizeUtils.formToOfferUpdateReq(values) });
        notifications.show({ title: 'Saved', message: `Code ${offer.code} updated.`, color: 'green' });
      }
      onClose();
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, `Failed to ${isCreate ? 'create' : 'save'} the code.`), color: 'red' });
    }
  }

  const productOptions = [{ value: '', label: 'Any product' }, ...products.filter((p) => p.status !== 'archived').map((p) => ({ value: p.id, label: p.name }))];

  return (
    <Drawer opened={opened} onClose={onClose} title={isCreate ? 'New promo code' : `Edit ${offer.code}`} position="right" size="md" padding="lg" closeOnClickOutside={!isPending} closeOnEscape={!isPending}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap="md">
          <CodeField control={form.control} disabled={!isCreate} />
          <NameField control={form.control} />
          <Divider label="Discount" labelPosition="left" />
          {isCreate ? (
            <>
              <DiscountTypeField control={form.control} />
              {discountType === 'percent' ? <PercentField control={form.control} /> : <AmountField control={form.control} />}
              <DurationField control={form.control} />
              {duration === 'repeating' && <MonthsField control={form.control} />}
            </>
          ) : (
            <Text size="sm">{MonetizeUtils.discountLabel(offer)} — a discount never changes once created; make a new code for a different one.</Text>
          )}
          <Divider label="Limits" labelPosition="left" />
          <ProductField control={form.control} options={productOptions} />
          <Group grow>
            <DateField control={form.control} name="startsAt" label="Valid from" />
            <DateField control={form.control} name="endsAt" label="Valid until" />
          </Group>
          <MaxRedemptionsField control={form.control} />
          <Group justify="flex-end" mt="lg">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={isPending}>{isCreate ? 'Create code' : 'Save changes'}</Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

type C = Control<OfferFormValues>;

function CodeField({ control, disabled }: { control: C; disabled: boolean }) {
  const { field, fieldState } = useController({ control, name: 'code' });
  return (
    <TextInput
      label="Code"
      withAsterisk
      description="What viewers type at checkout. Letters and digits; case does not matter."
      placeholder="SPRING25"
      maxLength={MONETIZE_CONSTRAINTS.CODE_MAX}
      disabled={disabled}
      value={field.value}
      onChange={(e) => field.onChange(e.currentTarget.value.toUpperCase())}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      styles={{ input: { textTransform: 'uppercase', fontFamily: 'var(--mantine-font-family-monospace)' } }}
    />
  );
}

function NameField({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'name' });
  return <TextInput label="Name" withAsterisk description="For you; viewers see the code" maxLength={MONETIZE_CONSTRAINTS.NAME_MAX} value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={fieldState.error?.message} />;
}

function DiscountTypeField({ control }: { control: C }) {
  const { field } = useController({ control, name: 'discountType' });
  return <SegmentedControl data={[{ value: 'percent', label: 'Percent off' }, { value: 'amount', label: 'Amount off' }]} value={field.value} onChange={field.onChange} />;
}

function PercentField({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'percentOff' });
  return <NumberInput label="Percent off" suffix="%" min={1} max={100} allowDecimal={false} value={field.value} onChange={(v) => field.onChange(typeof v === 'number' ? v : 0)} error={fieldState.error?.message} maw={200} />;
}

function AmountField({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'amountOff' });
  return <NumberInput label="Amount off" prefix="$" decimalScale={2} fixedDecimalScale min={0.01} value={field.value} onChange={field.onChange} error={fieldState.error?.message} maw={200} />;
}

function DurationField({ control }: { control: C }) {
  const { field } = useController({ control, name: 'duration' });
  return (
    <Select
      label="Applies to"
      description="For subscriptions; a one-time purchase takes the discount once."
      data={OFFER_DURATIONS.map((d) => ({ value: d, label: OFFER_DURATION_LABELS[d] }))}
      value={field.value}
      onChange={(v) => v && field.onChange(v)}
      allowDeselect={false}
    />
  );
}

function MonthsField({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'durationMonths' });
  return <NumberInput label="Months" min={1} max={MONETIZE_CONSTRAINTS.MAX_OFFER_MONTHS} allowDecimal={false} value={field.value} onChange={(v) => field.onChange(typeof v === 'number' ? v : 1)} error={fieldState.error?.message} maw={200} />;
}

function ProductField({ control, options }: { control: C; options: { value: string; label: string }[] }) {
  const { field } = useController({ control, name: 'productId' });
  return <Select label="Product" description="Limit the code to one product" data={options} value={field.value} onChange={(v) => field.onChange(v ?? '')} allowDeselect={false} />;
}

function DateField({ control, name, label }: { control: C; name: 'startsAt' | 'endsAt'; label: string }) {
  const { field, fieldState } = useController({ control, name });
  return <DateTimeField label={label} clearable valueFormat="MMM D, YYYY h:mm A" value={field.value} onChange={(v) => field.onChange(v || null)} error={fieldState.error?.message} />;
}

function MaxRedemptionsField({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'maxRedemptions' });
  return <NumberInput label="Maximum uses" description="Empty = no limit" min={1} allowDecimal={false} value={field.value} onChange={field.onChange} error={fieldState.error?.message} maw={200} />;
}
